import { notify } from "@general-backoffice/core/index";
import type { BotConfig, ExchangeAccount, User } from "@investhinks/api-client";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";

import { exchangeAccountsEndpoints } from "../../api";
import UserSelect from "../users/UserSelect";

interface MoveExchangeAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  exchangeAccount: ExchangeAccount | null | undefined;
  currentUser: User | null | undefined;
  bot: BotConfig | null | undefined;
  onSuccess?: () => void;
}

const MoveExchangeAccountModal: React.FC<MoveExchangeAccountModalProps> = ({
  isOpen,
  onClose,
  exchangeAccount,
  currentUser,
  bot,
  onSuccess,
}) => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "exchangeAccount.moveAccountModal",
  });

  const [selectedUser, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const accountName = exchangeAccount?.name || exchangeAccount?.accountId || "Account";
  const isSameUser = selectedUser?.id === currentUser?.id;
  const hasActiveSubscription = bot?.subscription?.pm_status === "active";
  const canSubmit = selectedUser && !isSameUser && !isLoading && !hasActiveSubscription;

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setUser(null);
      setError(null);
      onClose();
    }
  }, [isLoading, onClose]);

  const handleSubmit = async () => {
    if (!canSubmit || !exchangeAccount || !currentUser || !selectedUser) return;

    setIsLoading(true);
    setError(null);

    try {
      await exchangeAccountsEndpoints.moveExchangeAccount({
        id: Number(exchangeAccount.id),
        fromUserId: Number(currentUser.id),
        toUserId: Number(selectedUser.id),
      });
      notify.success(t("success"));
      handleClose();
      onSuccess?.();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t("error");
      setError(errorMessage);
      notify.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered className="move-account-modal" size="md">
      <ModalHeader toggle={handleClose} className="border-0 pb-0">
        <div className="d-flex align-items-center">
          <div className="modal-icon-wrapper mr-3">
            <i className="fas fa-exchange-alt" />
          </div>
          <div>
            <h5 className="mb-0 font-weight-bold">{t("title")}</h5>
            <small className="text-muted">{accountName}</small>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="pt-4">
        {/* Transfer Flow Visualization */}
        <div className="transfer-flow">
          {/* From User Card */}
          <div className="transfer-card transfer-card-from">
            <div className="transfer-card-label">{t("currentOwner")}</div>
            <div className="transfer-card-content">
              <div className="user-avatar">
                {currentUser?.name?.charAt(0)?.toUpperCase() || currentUser?.email?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div className="user-info">
                <div className="user-name">{currentUser?.name || currentUser?.username || t("unknownUser")}</div>
                <div className="user-email">{currentUser?.email}</div>
              </div>
            </div>
          </div>

          {/* Arrow Connector */}
          <div className="transfer-arrow">
            <div className="arrow-line" />
            <div className="arrow-icon">
              <i className="fas fa-arrow-down" />
            </div>
            <div className="arrow-line" />
          </div>

          {/* To User Card */}
          <div className={`transfer-card transfer-card-to ${selectedUser ? "has-selection" : ""}`}>
            <div className="transfer-card-label">{t("newOwner")}</div>
            <div className="transfer-card-content">
              {selectedUser ? (
                <>
                  <div className="user-avatar user-avatar-new">
                    {selectedUser?.name?.charAt(0)?.toUpperCase() ||
                      selectedUser?.email?.charAt(0)?.toUpperCase() ||
                      "?"}
                  </div>
                  <div className="user-info">
                    <div className="user-name">{selectedUser?.name || selectedUser?.username || t("unknownUser")}</div>
                    <div className="user-email">{selectedUser?.email}</div>
                  </div>
                </>
              ) : (
                <div className="select-placeholder">
                  <i className="fas fa-user-plus mr-2" />
                  {t("selectUser")}
                </div>
              )}
            </div>
            <div className="mt-3">
              <UserSelect
                user={selectedUser}
                setUser={(u) => setUser(!Array.isArray(u) ? u : null)}
                placeholder={t("selectUser")}
                isDisabled={isLoading}
              />
            </div>
          </div>
        </div>

        {/* Same User Warning */}
        {isSameUser && (
          <Alert color="warning" className="mt-3 mb-0 same-user-alert">
            <i className="fas fa-exclamation-triangle mr-2" />
            {t("sameUserWarning")}
          </Alert>
        )}

        {/* Active Subscription Warning */}
        {hasActiveSubscription && (
          <Alert color="danger" className="mt-3 mb-0 subscription-alert">
            <div className="d-flex align-items-start">
              <i className="fas fa-lock mr-3 mt-1" style={{ fontSize: "1.25rem" }} />
              <div>
                <strong>{t("subscriptionBlockTitle")}</strong>
                <p className="mb-0 mt-1" style={{ fontSize: "0.875rem" }}>
                  {t("subscriptionBlockMessage")}
                </p>
              </div>
            </div>
          </Alert>
        )}

        {/* Bots Warning */}
        <div className="warning-box mt-4">
          <div className="warning-icon">
            <i className="fas fa-robot" />
          </div>
          <div className="warning-content">
            <div className="warning-title">{t("warningTitle")}</div>
            <div className="warning-text">{t("warning")}</div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <Alert color="danger" className="mt-3 mb-0">
            <i className="fas fa-times-circle mr-2" />
            {error}
          </Alert>
        )}
      </ModalBody>

      <ModalFooter className="border-0 pt-0">
        <Button color="light" onClick={handleClose} disabled={isLoading} className="btn-cancel">
          {t("cancel")}
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={!canSubmit} className="btn-confirm">
          {isLoading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              {t("moving")}
            </>
          ) : (
            <>
              <i className="fas fa-check mr-2" />
              {t("confirm")}
            </>
          )}
        </Button>
      </ModalFooter>

      <style>{`
        .move-account-modal .modal-content {
          border: none;
          border-radius: 12px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        .move-account-modal .modal-header {
          padding: 1.5rem 1.5rem 0.5rem;
        }

        .modal-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #5e72e4 0%, #825ee4 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.25rem;
        }

        .move-account-modal .modal-body {
          padding: 1rem 1.5rem 1.5rem;
        }

        .move-account-modal .modal-footer {
          padding: 1rem 1.5rem 1.5rem;
          gap: 0.75rem;
        }

        /* Transfer Flow */
        .transfer-flow {
          display: flex;
          flex-direction: column;
          align-items: stretch;
        }

        .transfer-card {
          background: #f8f9fa;
          border-radius: 10px;
          padding: 1rem;
          transition: all 0.3s ease;
        }

        .transfer-card-from {
          border: 2px solid #e9ecef;
        }

        .transfer-card-to {
          border: 2px dashed #dee2e6;
        }

        .transfer-card-to.has-selection {
          border: 2px solid #5e72e4;
          background: linear-gradient(135deg, rgba(94, 114, 228, 0.05) 0%, rgba(130, 94, 228, 0.05) 100%);
        }

        .transfer-card-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #8898aa;
          font-weight: 600;
          margin-bottom: 0.75rem;
        }

        .transfer-card-content {
          display: flex;
          align-items: center;
          gap: 0.875rem;
          min-height: 48px;
        }

        .user-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #adb5bd 0%, #868e96 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 1.125rem;
          flex-shrink: 0;
        }

        .user-avatar-new {
          background: linear-gradient(135deg, #5e72e4 0%, #825ee4 100%);
          animation: avatarPulse 2s ease-in-out infinite;
        }

        @keyframes avatarPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(94, 114, 228, 0.4); }
          50% { box-shadow: 0 0 0 8px rgba(94, 114, 228, 0); }
        }

        .user-info {
          flex: 1;
          min-width: 0;
        }

        .user-name {
          font-weight: 600;
          color: #32325d;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-email {
          font-size: 0.8125rem;
          color: #8898aa;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .select-placeholder {
          color: #adb5bd;
          font-style: italic;
        }

        /* Arrow */
        .transfer-arrow {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0.5rem 0;
        }

        .arrow-line {
          width: 2px;
          height: 12px;
          background: linear-gradient(to bottom, #dee2e6, #5e72e4);
        }

        .arrow-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: white;
          border: 2px solid #5e72e4;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #5e72e4;
          font-size: 0.875rem;
          animation: arrowBounce 1.5s ease-in-out infinite;
        }

        @keyframes arrowBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(3px); }
        }

        /* Warning Box */
        .warning-box {
          display: flex;
          gap: 1rem;
          padding: 1rem;
          background: linear-gradient(135deg, rgba(251, 99, 64, 0.08) 0%, rgba(251, 177, 60, 0.08) 100%);
          border-radius: 10px;
          border: 1px solid rgba(251, 99, 64, 0.2);
        }

        .warning-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: linear-gradient(135deg, #fb6340 0%, #fbb13c 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1rem;
          flex-shrink: 0;
        }

        .warning-content {
          flex: 1;
        }

        .warning-title {
          font-weight: 600;
          color: #fb6340;
          font-size: 0.875rem;
          margin-bottom: 0.25rem;
        }

        .warning-text {
          font-size: 0.8125rem;
          color: #525f7f;
          line-height: 1.5;
        }

        /* Same User Alert */
        .same-user-alert {
          border-radius: 10px;
          border: none;
        }

        /* Buttons */
        .btn-cancel {
          padding: 0.625rem 1.25rem;
          border-radius: 8px;
          font-weight: 500;
        }

        .btn-confirm {
          padding: 0.625rem 1.5rem;
          border-radius: 8px;
          font-weight: 500;
          background: linear-gradient(135deg, #5e72e4 0%, #825ee4 100%);
          border: none;
          box-shadow: 0 4px 6px rgba(94, 114, 228, 0.25);
          transition: all 0.2s ease;
        }

        .btn-confirm:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 7px 14px rgba(94, 114, 228, 0.3);
        }

        .btn-confirm:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </Modal>
  );
};

export default MoveExchangeAccountModal;
